import {
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import {
  removeAuthorisationToken,
  storeAuthorisationToken,
} from "app/store/slices/authorisation";
import { fetchToken } from "ui/utils/ManageTokens";
import qs from "qs";
import { API_URL } from "app/config";
import { OfflineSkipResponse } from "./types/common/responses/OfflineSkipResponse";
import { hideAuthModal, showAuthModal } from "app/store/slices/authModal";
import { RootState } from "../store";
import {
  resetAccessCheck,
  selectShouldShowOverlay,
} from "app/store/slices/overlay";
import { addFailedRequest } from "../../store/slices/failedRequests";
import { formatLocalDateTime } from "ui/utils/dateUtils";
import { SessionService } from "../session/SessionService";

const selectAuthToken = (state) => state.auth?.token;

export const DynamicBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  {
    offlineSkip?: boolean;
  }
> = async (args, api, extraOptions) => {
  const baseQuery = fetchBaseQuery({
    baseUrl: API_URL,
    headers: {
      Accept: "application/json",
    },
    paramsSerializer: (params) =>
      qs.stringify(params, { arrayFormat: "brackets" }),
    prepareHeaders: async (headers) => {
      const state = api.getState();
      let token = selectAuthToken(state);
      if (!token) {
        const auth = await fetchToken();
        if (auth) {
          api.dispatch(storeAuthorisationToken(auth));
          token = auth.token_data.token;
        }
      }
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }

      return headers;
    },
  });

  const result = await baseQuery(args, api, extraOptions);

  if (extraOptions?.offlineSkip && result.error?.status === "FETCH_ERROR") {
    console.log(result);

    // Return a successful response
    return {
      data: {
        is_offline: true,
      } as OfflineSkipResponse,
      meta: result.meta,
    };
  }
  if (result.error && result.error.status === 401) {
    const state = api.getState() as RootState;

    const hasToken = !!state?.authorisation?.auth?.token;
    const { tokenLoaded, tokenRestored } = state?.overlay!;
    const { lastLoginTimestamp, sessionWarningShown } = state?.authModal!;

    const sessionState = {
      lastLoginTimestamp,
      sessionWarningShown,
      isLoggedIn: hasToken,
    };

    if (selectShouldShowOverlay({ overlay: state.overlay! })) {
      api.dispatch(removeAuthorisationToken());
      api.dispatch(resetAccessCheck());
      api.dispatch(hideAuthModal());
    } else if (
      tokenLoaded &&
      tokenRestored &&
      !hasToken &&
      SessionService.shouldShowModalFor401(sessionState)
    ) {
      api.dispatch(showAuthModal());
    } else {
      const debugInfo = SessionService.getDebugInfo(sessionState);
      console.log("🚫 Skipping modal:", {
        tokenLoaded,
        tokenRestored,
        hasToken,
        inCooldown: debugInfo.inCooldown,
        sessionWarningShown,
        timeSinceLogin: debugInfo.timeSinceLogin,
      });
    }
    return result;
  }

  if (result.error?.status === "FETCH_ERROR") {
    const requestConfig =
      typeof args === "string" ? { url: args, method: "GET" } : args;
    const method = requestConfig.method;

    const state = api.getState() as RootState;
    const token = state.authorisation?.auth?.token;

    if (
      (method === "POST" || method === "PUT" || method === "DELETE") &&
      requestConfig.url !== "/auth/login"
    ) {
      const failedRequest = {
        request_id: Date.now().toString(),
        endpoint: requestConfig.url,
        method: requestConfig.method!,
        payload: requestConfig.body,
        authorization: token ?? "",
        creation_date: formatLocalDateTime(new Date()),
        description: `${requestConfig.method} request to ${requestConfig.url}`,
      };
      api.dispatch(addFailedRequest(failedRequest));
    }
  }

  return result;
};
